import throttle from "lodash/throttle";
import debounce from "lodash/debounce";
import { isEventKey } from "keycode";
import store from "../store/index";

import { onLoadAndResize, updateMainContentOffset } from "../utils";
import { MainMenu } from "./main-menu";

const SELECTORS = {
  mainContent: 'main, [role="main"]',
  pageHeader: ".js-page-header",
  userBtn: ".js-nav-user",
  loginMenu: ".js-login-menu",
  loginDropdown: ".js-login-menu-dropdown",
  mainMenuBtn: ".js-nav-menu-btn",
  mainMenuMobile: ".js-main-menu-mobile",
  navContainer: ".js-nav-container",
  navTopIntersectSpy: ".js-nav-top-intersect-spy",
  navBottomIntersectSpy: ".js-nav-bottom-intersect-spy",
  searchBtnMobile: ".js-nav-search-btn",
  searchPanelMobile: ".js-search-panel-mobile",
  menuBtnMobileOverlay: ".js-menu-btn-mobile",
  searchBtnMobileOverlay: ".js-search-btn-dialog",
};

const CLASSES = {
  transparent: "nav-container--transparent",
  sticky: "nav--sticky",
  hide: "nav--hide",
  reveal: "nav--reveal",
  open: "open",
  hidden: "hidden",
  tabActive: "tab-active",
};

class Nav {
  constructor(context) {
    this.context = context;
    this.lastScrollPosition = 0;
    this.navTopPosition = 0;
    this.showNavbar = true;
    this.isNavTopIntersect = false;
    this.isNavBottomIntersect = false;
    this.mainMenu = new MainMenu(context);
  }

  init() {
    this.mainContent = document.querySelector(SELECTORS.mainContent);
    this.pageHeader = document.querySelector(SELECTORS.pageHeader);
    this.navContainer = document.querySelector(SELECTORS.navContainer);
    this.navTopIntersectSpy = this.navContainer.querySelector(
      SELECTORS.navTopIntersectSpy,
    );
    this.navBottomIntersectSpy = this.navContainer.querySelector(
      SELECTORS.navBottomIntersectSpy,
    );
    this.userMenuBtn = this.context.querySelector(SELECTORS.userBtn);
    this.loginMenu = this.context.querySelector(SELECTORS.loginMenu);
    this.loginDropdown = this.context.querySelector(SELECTORS.loginDropdown);
    this.mainMenuBtn = this.context.querySelector(SELECTORS.mainMenuBtn);
    this.mainMenuMobile = this.context.querySelector(SELECTORS.mainMenuMobile);
    this.searchBtnMobile = this.context.querySelector(
      SELECTORS.searchBtnMobile,
    );
    this.searchPanelMobile = this.context.querySelector(
      SELECTORS.searchPanelMobile,
    );
    this.menuBtnMobileOverlay = this.context.querySelector(
      SELECTORS.menuBtnMobileOverlay,
    );
    this.searchBtnMobileOverlay = this.context.querySelector(
      SELECTORS.searchBtnMobileOverlay,
    );

    this.navRevealHandler = this.handleNavReveal.bind(this);
    this.navTopIntersectionHandler = this.handleNavTopIntersection.bind(this);
    this.navBottomIntersectionHandler =
      this.handleNavBottomIntersection.bind(this);

    this.navTopIntersectionObserver = new IntersectionObserver(
      this.navTopIntersectionHandler,
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.0,
      },
    );

    this.navBottomIntersectionObserver = new IntersectionObserver(
      this.navBottomIntersectionHandler,
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.0,
      },
    );

    this.mainContentOffset = updateMainContentOffset(this.context);

    this.matchNavBottomIntersectPosition().apply();

    this.mainMenu.init();

    this.addListeners();
  }

  addListeners() {
    this.mainMenuBtn.addEventListener("click", (e) =>
      this.showMainMenuDialog(e),
    );

    if (this.searchBtnMobile) {
      this.searchBtnMobile.addEventListener("click", (e) =>
        this.showSearchPanel(e),
      );
    }

    if (this.userMenuBtn) {
      this.userMenuBtn.addEventListener("click", () =>
        this.toggleLoginDropdown(),
      );
      this.loginMenu.addEventListener("keydown", (e) =>
        this.handleLoginMenuKeydown(e),
      );
    }

    document.addEventListener("click", (e) => this.handleClickOutside(e));

    this.navTopIntersectionObserver.observe(this.navTopIntersectSpy);
    this.navBottomIntersectionObserver.observe(this.navBottomIntersectSpy);

    window.addEventListener("load", () => {
      window.requestIdleCallback(this.navRevealHandler);
    });

    window.addEventListener(
      "scroll",
      throttle(() => {
        window.requestIdleCallback(this.navRevealHandler);
      }),
      300,
      {
        leading: true,
        trailing: false,
      },
    );

    window.addEventListener(
      "scroll",
      debounce(() => {
        window.requestIdleCallback(this.navRevealHandler);
        this.closeLoginDropdown();
      }),
      400,
      {
        leading: false,
        trailing: true,
      },
    );
  }

  showSearchPanel(e) {
    if (this.menuBtnMobileOverlay) {
      this.menuBtnMobileOverlay.classList.remove(CLASSES.tabActive);
    }
    if (this.searchBtnMobileOverlay) {
      this.searchBtnMobileOverlay.classList.add(CLASSES.tabActive);
    }
    this.searchPanelMobile.classList.remove(CLASSES.hidden);
    this.mainMenuMobile.classList.add(CLASSES.hidden);

    this.mainMenu.mobileDialog.showDialog(e.currentTarget);
    store.dispatch("searchPanelMobileOpen", { searchPanelMobileOpen: true });
  }

  isTransparent() {
    return this.navContainer.classList.contains(CLASSES.transparent);
  }

  hide() {
    if (this.context.classList.contains(CLASSES.reveal)) {
      // Only trigger hide animation if nav was previously revealed
      this.context.classList.remove(CLASSES.hidden, CLASSES.reveal);
      this.context.classList.add(CLASSES.hide);
    }

    return this;
  }

  reveal() {
    this.context.classList.remove(CLASSES.hidden, CLASSES.hide);
    this.context.classList.add(CLASSES.reveal);

    return this;
  }

  stick() {
    if (!this.isTransparent()) {
      this.mainContentOffset.apply();
    }

    this.context.classList.add(CLASSES.hidden, CLASSES.sticky);

    return this;
  }

  unstick() {
    if (!this.isTransparent()) {
      this.mainContentOffset.remove();
    }

    this.context.classList.remove(CLASSES.hidden, CLASSES.sticky);

    return this;
  }

  setLastScrollPosition(scrollPosition) {
    this.lastScrollPosition = scrollPosition;

    return this;
  }

  matchNavBottomIntersectPosition() {
    return onLoadAndResize(
      () => {
        this.navBottomIntersectSpy.style.top = `${
          this.context.offsetHeight - 1
        }px`;
      },
      () => {
        this.navBottomIntersectSpy.style.top = null;
      },
    );
  }

  handleClickOutside({ target }) {
    if (this.userMenuBtn) {
      const isClickOutside = !this.loginMenu.contains(target);

      if (isClickOutside) {
        this.closeLoginDropdown();
      }
    }
  }

  handleLoginMenuKeydown(e) {
    if (isEventKey(e, "esc")) {
      if (this.userMenuBtn) {
        this.closeLoginDropdown();
      }
    }
  }

  isLoginDropdownOpen() {
    return this.loginDropdown.classList.contains(CLASSES.open);
  }

  openLoginDropdown() {
    this.userMenuBtn.setAttribute("aria-expanded", "true");
    this.userMenuBtn.classList.add(CLASSES.open);
    this.loginDropdown.classList.add(CLASSES.open);

    return this;
  }

  closeLoginDropdown() {
    this.userMenuBtn.setAttribute("aria-expanded", "false");
    this.userMenuBtn.classList.remove(CLASSES.open);
    this.loginDropdown.classList.remove(CLASSES.open);

    return this;
  }

  toggleLoginDropdown() {
    if (this.isLoginDropdownOpen()) {
      this.closeLoginDropdown();
    } else {
      this.openLoginDropdown();
    }

    return this;
  }

  handleNavTopIntersection(entries, observer) {
    window.requestAnimationFrame(() => {
      const latestEntry = entries.pop();

      this.isNavTopIntersect = latestEntry.isIntersecting;

      if (this.isNavTopIntersect) {
        // Unstick as soon as top nav spy is scrolled by and becomes visible
        this.unstick();
      }
    });
  }

  handleNavBottomIntersection(entries, observer) {
    window.requestAnimationFrame(() => {
      const latestEntry = entries.pop();

      this.isNavBottomIntersect = latestEntry.isIntersecting;

      if (!this.isNavBottomIntersect) {
        // Stick as soon as bottom nav spy is scrolled by and becomes invisible
        this.stick();
      }
    });
  }

  handleNavReveal() {
    const currentScrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    // Because of momentum scrolling on mobiles, we shouldn't continue if it is less than zero
    if (currentScrollPosition < 0) {
      return;
    }

    if (
      currentScrollPosition === this.lastScrollPosition ||
      Math.abs(currentScrollPosition - this.lastScrollPosition) < 1
    ) {
      return;
    }

    const isScrollingUp = currentScrollPosition < this.lastScrollPosition;
    const isInitScrolled =
      this.lastScrollPosition === 0 && !this.isNavBottomIntersect;
    this.showNavbar = isScrollingUp || isInitScrolled;

    window.requestAnimationFrame(() => {
      if (this.isNavBottomIntersect) {
        this.context.classList.remove(CLASSES.hide, CLASSES.reveal);
      } else if (this.showNavbar) {
        this.reveal();
      } else {
        this.hide();
      }

      this.setLastScrollPosition(currentScrollPosition);
    });
  }

  showMainMenuDialog(event) {
    this.mainMenu.mobileDialog.showDialog(event.currentTarget);
    store.dispatch("mainMenuMobileOpen", { mainMenuMobileOpen: true });
  }
}

export { Nav };
